import Link from '@/routing/AppLink';
import { useRouter } from '@/routing/useAppRouter';
import { useTranslation } from 'next-i18next/pages';
import { Fragment, useRef } from 'react';

import useAppsHeaderOptionData from '@/hooks/apps/useAppsHeaderOptionData';
import { useHeader } from '@/hooks/apps/useHeader';

import LogoBlack from '../../ui/header/LogoBlack';
import LogoWhite from '../../ui/header/LogoWhite';
import { IconFacebook, IconLinkedIn, IconYoutube } from '../../ui/icon';
import SelectLanguage from '../../ui/selectLanguage/SelectLanguage';
import Area from '../shared/Area';
import { HeaderSubType } from '../shared/HeaderSub';
import * as s from './Header.styles';
import { HeaderNav } from './HeaderNav';

export type HeaderVariantType = 'transparent' | 'default';

type Props = {
  hideMenu?: boolean;
  headerSubComponent?: HeaderSubType | null;
};

export default function Header({ hideMenu, headerSubComponent }: Props) {
  const headerContainerRef = useRef<HTMLElement>(null);

  const { isHeaderChanges } = useAppsHeaderOptionData();

  const { t } = useTranslation('common');

  const { isFixed, variantStyle, isVisible } = useHeader({
    isHeaderChanges,
  });

  const router = useRouter();

  const activePath = (pathname: string) => {
    if (pathname === '/') {
      return router.pathname === pathname;
    } else {
      return (
        router.pathname.startsWith(pathname) ||
        router.asPath.startsWith(pathname)
      );
    }
  };

  const renderLogo = () =>
    !isHeaderChanges || isFixed ? <LogoBlack /> : <LogoWhite />;

  return (
    <header
      ref={headerContainerRef}
      className={s.headerContainer(isVisible, variantStyle)}
    >
      <Area>
        <div className={s.headerWrapper}>
          <div className={s.leftContainer}>
            <Link href="/" aria-label={t('디플리 홈')} prefetch={false}>
              {renderLogo()}
            </Link>
          </div>
          {!hideMenu && (
            <Fragment>
              {/* Remount on navigation so a submenu held open by focus does not survive the page change */}
              <HeaderNav
                key={router.asPath}
                variant={variantStyle}
                isActivePath={activePath}
              />
              <div className={s.rightContainer}>
                <div className={s.socialLinks}>
                  <a
                    href="https://www.linkedin.com/company/deeplyinc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.socialLink(variantStyle)}
                    aria-label={t('LinkedIn에서 디플리 보기')}
                  >
                    <IconLinkedIn size={28} aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.facebook.com/deeplyinc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.socialLink(variantStyle)}
                    aria-label={t('Facebook에서 디플리 보기')}
                  >
                    <IconFacebook size={28} aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UCiEkyVJS5JMzp-0DxAamcQg/videos?view=0&sort=p"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.socialLink(variantStyle)}
                    aria-label={t('YouTube에서 디플리 보기')}
                  >
                    <IconYoutube size={28} aria-hidden="true" />
                  </a>
                </div>
                <SelectLanguage variant={variantStyle} />
              </div>
            </Fragment>
          )}
        </div>
      </Area>

      {headerSubComponent && headerSubComponent}
    </header>
  );
}
